/**
 * Audit log entries — comprehensive, industry-standard event log.
 *
 * Every mutating action across the platform produces one of these. Entries
 * are tamper-evident (server-side hash chain) and read-only in the UI.
 *
 * Field shape mirrors NESTJS_SCHEMA.md `audit_logs` table.
 */

export type AuditCategory =
  | 'authentication'
  | 'user_management'
  | 'business'
  | 'subscription'
  | 'payment'
  | 'configuration'
  | 'security'
  | 'system'
  | 'platform'
  | 'reseller'
  | 'billing';

export type AuditSeverity = 'info' | 'warning' | 'critical';
export type AuditOutcome = 'success' | 'failure';
export type ActorRole = 'platform_admin' | 'reseller_admin' | 'business_admin' | 'business_staff' | 'system';

export type AuditEntry = {
  id: string;
  /** ISO timestamp with timezone — the single date source; render via lib/format. */
  occurredAt: string;

  /** Who did it */
  actor: string;
  actorEmail: string;
  actorRole: ActorRole;

  /** What they did */
  action: string;
  category: AuditCategory;
  severity: AuditSeverity;
  outcome: AuditOutcome;

  /** What it was done to */
  subjectType:
    | 'user'
    | 'business'
    | 'reseller'
    | 'subscription'
    | 'invoice'
    | 'payment'
    | 'package'
    | 'coupon'
    | 'domain'
    | 'webhook'
    | 'system'
    | 'session'
    | 'mfa';
  subjectId: string;
  target: string;

  /** Where they did it from */
  ip: string;
  ipCountry: string;
  userAgent: string;
  sessionId: string;
  requestId: string;

  /** Optional fields populated based on event type */
  diff?: Record<string, { before: unknown; after: unknown }>;
  metadata?: Record<string, unknown>;
};

/* eslint-disable max-len */
export const auditLog: AuditEntry[] = [
  {
    id: 'al_n01',
    occurredAt: '2026-05-06T11:42:18Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@itcitysoft.com',
    actorRole: 'platform_admin',
    action: 'gateway.connected',
    category: 'platform',
    severity: 'info',
    outcome: 'success',
    subjectType: 'system',
    subjectId: 'gw_stripe',
    target: 'Stripe · live',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_n01a2b',
    metadata: { mode: 'live', latency_ms: 318, account_id: 'acct_1Q…X9' },
  },
  {
    id: 'al_n02',
    occurredAt: '2026-05-06T10:08:55Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@itcitysoft.com',
    actorRole: 'platform_admin',
    action: 'broadcast.sent',
    category: 'platform',
    severity: 'info',
    outcome: 'success',
    subjectType: 'system',
    subjectId: 'bcast_412',
    target: 'Maintenance window · 412 end customers',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_n02c3d',
    metadata: { recipients: 412, channels: ['email', 'in_app'], scheduled_at: '2026-05-08T20:00:00Z' },
  },
  {
    id: 'al_n03',
    occurredAt: '2026-05-06T09:33:44Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'login.success',
    category: 'security',
    severity: 'info',
    outcome: 'success',
    subjectType: 'session',
    subjectId: 'ses_n03',
    target: 'Imran Khan · Sylhet, BD',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_n03',
    requestId: 'req_n03e4f',
    metadata: { device: 'Mac', browser: 'Chrome 126', location: 'Sylhet, BD', mfa_verified: true },
  },
  {
    id: 'al_n04',
    occurredAt: '2026-05-05T22:14:09Z',
    actor: 'unknown',
    actorEmail: 'unknown@example.com',
    actorRole: 'system',
    action: 'login.failed',
    category: 'security',
    severity: 'warning',
    outcome: 'failure',
    subjectType: 'user',
    subjectId: 'unknown',
    target: 'unknown@example.com',
    ip: '103.85.21.4',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Windows) Edge/132',
    sessionId: '—',
    requestId: 'req_n04g5h',
    metadata: { reason: 'bad_password', attempts_in_burst: 3, captcha_required: false },
  },
  {
    id: 'al_n05',
    occurredAt: '2026-05-05T18:55:22Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@itcitysoft.com',
    actorRole: 'platform_admin',
    action: 'application.approved',
    category: 'reseller',
    severity: 'info',
    outcome: 'success',
    subjectType: 'reseller',
    subjectId: 'rsl_5',
    target: 'Tanvir IT · 15% commission',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_n05i6j',
    metadata: { commission_pct: 15, application_id: 'rsa_5', owner_email: 'tanvir@tanvir-it.bd' },
  },
  {
    id: 'al_n06',
    occurredAt: '2026-05-05T15:21:07Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@itcitysoft.com',
    actorRole: 'platform_admin',
    action: 'reseller.pricing_override.set',
    category: 'reseller',
    severity: 'info',
    outcome: 'success',
    subjectType: 'reseller',
    subjectId: 'rsl_1',
    target: 'Acme Pro · ৳1,100/mo override',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_n06k7l',
    diff: { override_bdt: { before: null, after: 1100 } },
    metadata: { tier: 'tier_pro', reseller_id: 'rsl_1' },
  },
  {
    id: 'al_n07',
    occurredAt: '2026-05-05T13:08:31Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@itcitysoft.com',
    actorRole: 'platform_admin',
    action: 'pricing_tier.edited',
    category: 'platform',
    severity: 'info',
    outcome: 'success',
    subjectType: 'package',
    subjectId: 'tier_pro',
    target: 'tier_pro · min monthly raised',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_n07m8n',
    diff: { min_monthly_bdt: { before: 1200, after: 1300 } },
  },
  {
    id: 'al_n08',
    occurredAt: '2026-05-05T11:55:48Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@itcitysoft.com',
    actorRole: 'platform_admin',
    action: 'pricing_tier.created',
    category: 'platform',
    severity: 'info',
    outcome: 'success',
    subjectType: 'package',
    subjectId: 'tier_premium',
    target: 'tier_premium · ৳1,800/mo',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_n08o9p',
    metadata: { min_monthly_bdt: 1800, tier_key: 'tier_premium' },
  },
  {
    id: 'al_n09',
    occurredAt: '2026-05-05T08:24:17Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'mfa.enabled',
    category: 'security',
    severity: 'info',
    outcome: 'success',
    subjectType: 'mfa',
    subjectId: 'usr_10',
    target: 'Imran Khan · Authy',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_n03',
    requestId: 'req_n09q0r',
    metadata: { method: 'TOTP', provider: 'Authy', backup_codes_generated: 8 },
  },
  {
    id: 'al_n10',
    occurredAt: '2026-05-04T20:12:33Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@itcitysoft.com',
    actorRole: 'platform_admin',
    action: 'session.admin_revoked',
    category: 'security',
    severity: 'warning',
    outcome: 'success',
    subjectType: 'session',
    subjectId: 'usr_10',
    target: 'All sessions for imran@khan-bakery.com',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_n10s1t',
    metadata: { revoked_count: 3, target_user: 'usr_10', reason: 'support_request' },
  },
  {
    id: 'al_n11',
    occurredAt: '2026-05-04T17:42:55Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'session.cap_exceeded',
    category: 'security',
    severity: 'warning',
    outcome: 'success',
    subjectType: 'session',
    subjectId: 'ses_revoked_old',
    target: 'Mac · Chrome 126 (oldest session revoked)',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (iPhone) Safari/17',
    sessionId: 'ses_n11',
    requestId: 'req_n11u2v',
    metadata: { device_count: 6, max_sessions: 5, oldest_device: 'Mac · Chrome 126' },
  },
  {
    id: 'al_n12',
    occurredAt: '2026-05-04T14:30:21Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'device.new_signin',
    category: 'security',
    severity: 'info',
    outcome: 'success',
    subjectType: 'session',
    subjectId: 'ses_iphone',
    target: 'iPhone · Safari · Sylhet, BD',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (iPhone) Safari/17',
    sessionId: 'ses_iphone',
    requestId: 'req_n12w3x',
    metadata: { device: 'iPhone', browser: 'Safari', location: 'Sylhet, BD', first_seen: true },
  },
  {
    id: 'al_n13',
    occurredAt: '2026-05-04T11:15:44Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'mfa.recovery_code_used',
    category: 'security',
    severity: 'warning',
    outcome: 'success',
    subjectType: 'mfa',
    subjectId: 'usr_10',
    target: 'Recovery code consumed during sign-in',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_n13',
    requestId: 'req_n13y4z',
    metadata: { codes_remaining: 7, reason: 'lost_authy_device' },
  },
  {
    id: 'al_n14',
    occurredAt: '2026-05-04T09:08:12Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'mfa.disabled',
    category: 'security',
    severity: 'warning',
    outcome: 'success',
    subjectType: 'mfa',
    subjectId: 'usr_10',
    target: 'Imran Khan · 2FA off',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_n13',
    requestId: 'req_n14a5b',
    metadata: { method: 'TOTP', password_verified: true, reason: 'switching_provider' },
  },
  {
    id: 'al_n15',
    occurredAt: '2026-05-03T21:50:08Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@itcitysoft.com',
    actorRole: 'platform_admin',
    action: 'package_category.created',
    category: 'platform',
    severity: 'info',
    outcome: 'success',
    subjectType: 'package',
    subjectId: 'pkgcat_school',
    target: "Category 'School' · provisions_pos: false",
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_n15c6d',
    metadata: { provisions_pos: false, slug: 'school' },
  },
  {
    id: 'al_n16',
    occurredAt: '2026-05-03T19:18:42Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@itcitysoft.com',
    actorRole: 'platform_admin',
    action: 'currency.activated',
    category: 'platform',
    severity: 'info',
    outcome: 'success',
    subjectType: 'system',
    subjectId: 'currency_gbp',
    target: 'GBP · activated platform-wide',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/126',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_n16e7f',
    diff: { active: { before: false, after: true } },
    metadata: { currency_code: 'GBP', symbol: '£' },
  },
  {
    id: 'al_001',
    occurredAt: '2026-05-03T14:32:08Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@rootsoftit.com',
    actorRole: 'platform_admin',
    action: 'business.suspend',
    category: 'business',
    severity: 'warning',
    outcome: 'success',
    subjectType: 'business',
    subjectId: 'biz_42',
    target: 'Khan Bakery',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_8f2a1c',
    metadata: { reason: 'past_due_30d', dunning_attempts: 4 },
  },
  {
    id: 'al_002',
    occurredAt: '2026-05-03T14:11:42Z',
    actor: 'system',
    actorEmail: 'system@possignin',
    actorRole: 'system',
    action: 'db.provisioned',
    category: 'business',
    severity: 'info',
    outcome: 'success',
    subjectType: 'business',
    subjectId: 'biz_198',
    target: 'Greenfields Mart',
    ip: '10.0.4.12',
    ipCountry: 'BD',
    userAgent: 'possignin-provisioner/1.4.2',
    sessionId: 'ses_system',
    requestId: 'req_9b3c8d',
    metadata: { db_host: 'db-mgr-01.internal', db_size_mb: 12, took_ms: 32420 },
  },
  {
    id: 'al_003',
    occurredAt: '2026-05-03T09:02:01Z',
    actor: 'Rakib Ahmed',
    actorEmail: 'rakib@acme-resellers.com',
    actorRole: 'reseller_admin',
    action: 'branding.update',
    category: 'configuration',
    severity: 'info',
    outcome: 'success',
    subjectType: 'reseller',
    subjectId: 'rsl_1',
    target: 'Acme Resellers',
    ip: '103.85.21.4',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Windows) Edge/132',
    sessionId: 'ses_3d4e5f',
    requestId: 'req_aa11bb',
    diff: {
      primary_color: { before: '#0F766E', after: '#0E6663' },
      logo_url: { before: 'acme-v1.svg', after: 'acme-v2.svg' },
    },
  },
  {
    id: 'al_004',
    occurredAt: '2026-05-03T08:44:17Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@rootsoftit.com',
    actorRole: 'platform_admin',
    action: 'payment.cash_recorded',
    category: 'payment',
    severity: 'info',
    outcome: 'success',
    subjectType: 'invoice',
    subjectId: 'INV-2026-0345',
    target: 'INV-2026-0345 · Khan Bakery · ৳300',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_22cd33',
    metadata: { amount_bdt: 300, receipt_no: 'OFF-4421', received_by: 'Sayem K' },
  },
  {
    id: 'al_005',
    occurredAt: '2026-05-03T07:15:32Z',
    actor: 'system',
    actorEmail: 'system@possignin',
    actorRole: 'system',
    action: 'webhook.delivered',
    category: 'system',
    severity: 'info',
    outcome: 'success',
    subjectType: 'webhook',
    subjectId: 'wh_evt_8273',
    target: 'business.status_changed → pos.legacy',
    ip: '10.0.4.12',
    ipCountry: 'BD',
    userAgent: 'possignin-webhooks/2.0.1',
    sessionId: 'ses_system',
    requestId: 'req_dd44ee',
    metadata: { http_status: 200, attempt: 1, latency_ms: 142 },
  },
  {
    id: 'al_006',
    occurredAt: '2026-05-03T06:48:11Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'auth.login_failed',
    category: 'authentication',
    severity: 'warning',
    outcome: 'failure',
    subjectType: 'user',
    subjectId: 'usr_10',
    target: 'Imran Khan',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (iPhone) Safari/17',
    sessionId: '—',
    requestId: 'req_55ff66',
    metadata: { reason: 'wrong_password', attempts_24h: 2 },
  },
  {
    id: 'al_007',
    occurredAt: '2026-05-02T22:18:54Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'auth.login',
    category: 'authentication',
    severity: 'info',
    outcome: 'success',
    subjectType: 'session',
    subjectId: 'ses_new_5',
    target: 'Imran Khan',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_new_5',
    requestId: 'req_77aa88',
    metadata: { mfa_verified: true, new_device: false },
  },
  {
    id: 'al_008',
    occurredAt: '2026-05-02T17:12:04Z',
    actor: 'Samira Khatun',
    actorEmail: 'samira@acme-resellers.com',
    actorRole: 'reseller_admin',
    action: 'staff.invite',
    category: 'user_management',
    severity: 'info',
    outcome: 'success',
    subjectType: 'user',
    subjectId: 'u8',
    target: 'nayem@acme-resellers.com',
    ip: '103.85.21.7',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_4f5g6h',
    requestId: 'req_99cc00',
    metadata: { role: 'staff', expires_in_h: 48 },
  },
  {
    id: 'al_009',
    occurredAt: '2026-05-02T16:01:33Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'subscription.cancel',
    category: 'subscription',
    severity: 'warning',
    outcome: 'success',
    subjectType: 'subscription',
    subjectId: 'sub_2',
    target: 'Khan Bakery · Starter',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_new_5',
    requestId: 'req_11dd22',
    metadata: { reason: 'too_expensive', cancel_at_period_end: true, period_end: '2026-06-03' },
  },
  {
    id: 'al_010',
    occurredAt: '2026-05-02T11:24:08Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@rootsoftit.com',
    actorRole: 'platform_admin',
    action: 'package.publish',
    category: 'configuration',
    severity: 'info',
    outcome: 'success',
    subjectType: 'package',
    subjectId: 'pkg_extended',
    target: 'Extended (1500/mo)',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_33ee44',
    diff: { status: { before: 'draft', after: 'published' } },
  },
  {
    id: 'al_011',
    occurredAt: '2026-05-02T04:14:22Z',
    actor: 'system',
    actorEmail: 'system@possignin',
    actorRole: 'system',
    action: 'replica.lag_alert',
    category: 'system',
    severity: 'critical',
    outcome: 'success',
    subjectType: 'system',
    subjectId: 'mariadb-replica-01',
    target: 'mariadb-replica-01',
    ip: '10.0.4.12',
    ipCountry: 'BD',
    userAgent: 'possignin-monitor/1.2.0',
    sessionId: 'ses_system',
    requestId: 'req_55gg66',
    metadata: { lag_sec: 18, threshold_sec: 5, duration_min: 4 },
  },
  {
    id: 'al_012',
    occurredAt: '2026-05-01T20:33:07Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'mfa.enable',
    category: 'security',
    severity: 'info',
    outcome: 'success',
    subjectType: 'mfa',
    subjectId: 'usr_10',
    target: 'Imran Khan',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_old_5',
    requestId: 'req_77hh88',
    metadata: { method: 'TOTP', backup_codes_generated: 8 },
  },
  {
    id: 'al_013',
    occurredAt: '2026-05-01T14:19:45Z',
    actor: 'unknown',
    actorEmail: 'unknown',
    actorRole: 'system',
    action: 'auth.login_failed',
    category: 'security',
    severity: 'critical',
    outcome: 'failure',
    subjectType: 'user',
    subjectId: 'u1',
    target: 'sayem@rootsoftit.com',
    ip: '45.158.32.119',
    ipCountry: 'RU',
    userAgent: 'curl/7.88',
    sessionId: '—',
    requestId: 'req_99ii00',
    metadata: { reason: 'wrong_password', attempts_in_burst: 11, captcha_required: true, ip_blocked_for_min: 15 },
  },
  {
    id: 'al_014',
    occurredAt: '2026-05-01T12:42:11Z',
    actor: 'Mahfuz Rahman',
    actorEmail: 'admin@sundarbantech.com',
    actorRole: 'reseller_admin',
    action: 'reseller.payout.request',
    category: 'payment',
    severity: 'info',
    outcome: 'success',
    subjectType: 'reseller',
    subjectId: 'rsl_2',
    target: 'Sundarban Tech',
    ip: '103.10.45.22',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_77jj88',
    requestId: 'req_aa22bb',
    metadata: { amount_bdt: 12300, period: '2026-04', method: 'bank_transfer' },
  },
  {
    id: 'al_015',
    occurredAt: '2026-05-01T10:08:55Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@rootsoftit.com',
    actorRole: 'platform_admin',
    action: 'gateway.test_connection',
    category: 'configuration',
    severity: 'info',
    outcome: 'success',
    subjectType: 'system',
    subjectId: 'sslcommerz',
    target: 'SSLCommerz · live',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_cc33dd',
    metadata: { mode: 'live', latency_ms: 244 },
  },
  {
    id: 'al_016',
    occurredAt: '2026-04-30T19:25:18Z',
    actor: 'Manager (Greenfields)',
    actorEmail: 'manager@greenfields.bd',
    actorRole: 'business_admin',
    action: 'business.create',
    category: 'business',
    severity: 'info',
    outcome: 'success',
    subjectType: 'business',
    subjectId: 'biz_198',
    target: 'Greenfields Mart',
    ip: '180.211.45.61',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Windows) Chrome/132',
    sessionId: 'ses_kk55ll',
    requestId: 'req_ee44ff',
    metadata: { plan: 'pkg_enterprise', via_reseller: 'rsl_1', trial_days: 7 },
  },
  {
    id: 'al_017',
    occurredAt: '2026-04-30T18:51:02Z',
    actor: 'system',
    actorEmail: 'system@possignin',
    actorRole: 'system',
    action: 'payment.refund',
    category: 'payment',
    severity: 'warning',
    outcome: 'success',
    subjectType: 'payment',
    subjectId: 'pay_8',
    target: 'INV-2026-0212 · Khan Bakery · ৳300',
    ip: '10.0.4.12',
    ipCountry: 'BD',
    userAgent: 'sslcommerz-webhook/3.1',
    sessionId: 'ses_system',
    requestId: 'req_77kk88',
    metadata: { reason: 'duplicate_charge', refund_id: 'rfd_4f3a' },
  },
  {
    id: 'al_018',
    occurredAt: '2026-04-29T22:01:33Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@rootsoftit.com',
    actorRole: 'platform_admin',
    action: 'package.deprecate',
    category: 'configuration',
    severity: 'warning',
    outcome: 'success',
    subjectType: 'package',
    subjectId: 'pkg_legacy_pro',
    target: 'Pro v1 (deprecated)',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_99mm00',
    metadata: { active_subscribers: 31, notice_period_days: 60 },
  },
  {
    id: 'al_019',
    occurredAt: '2026-04-29T15:18:44Z',
    actor: 'Rakib Ahmed',
    actorEmail: 'rakib@acme-resellers.com',
    actorRole: 'reseller_admin',
    action: 'domain.verify',
    category: 'configuration',
    severity: 'info',
    outcome: 'success',
    subjectType: 'domain',
    subjectId: 'dom_2',
    target: 'acme-resellers.com',
    ip: '103.85.21.4',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Windows) Edge/132',
    sessionId: 'ses_3d4e5f',
    requestId: 'req_aa44bb',
    metadata: { verification_method: 'CNAME', took_attempts: 1, tls_status: 'issued' },
  },
  {
    id: 'al_020',
    occurredAt: '2026-04-28T11:08:21Z',
    actor: 'Samira Khatun',
    actorEmail: 'samira@acme-resellers.com',
    actorRole: 'reseller_admin',
    action: 'role.change',
    category: 'user_management',
    severity: 'warning',
    outcome: 'success',
    subjectType: 'user',
    subjectId: 'u3',
    target: 'samira@acme-resellers.com',
    ip: '103.85.21.7',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_4f5g6h',
    requestId: 'req_cc55dd',
    diff: { role: { before: 'staff', after: 'admin' } },
  },
  {
    id: 'al_021',
    occurredAt: '2026-04-27T20:47:09Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'data.export',
    category: 'security',
    severity: 'info',
    outcome: 'success',
    subjectType: 'business',
    subjectId: 'biz_42',
    target: 'Khan Bakery · GDPR-style export',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_old_5',
    requestId: 'req_ee66ff',
    metadata: { format: 'json+csv', size_mb: 4.2, sha256: '7a3b…f29c' },
  },
  {
    id: 'al_022',
    occurredAt: '2026-04-26T08:33:55Z',
    actor: 'system',
    actorEmail: 'system@possignin',
    actorRole: 'system',
    action: 'backup.complete',
    category: 'system',
    severity: 'info',
    outcome: 'success',
    subjectType: 'business',
    subjectId: 'biz_42',
    target: 'Khan Bakery · daily backup',
    ip: '10.0.4.12',
    ipCountry: 'BD',
    userAgent: 'possignin-backup/1.0',
    sessionId: 'ses_system',
    requestId: 'req_77nn88',
    metadata: { size_mb: 412, took_ms: 18420, retention_days: 7 },
  },
  {
    id: 'al_023',
    occurredAt: '2026-04-25T18:14:30Z',
    actor: 'system',
    actorEmail: 'system@possignin',
    actorRole: 'system',
    action: 'subscription.past_due',
    category: 'subscription',
    severity: 'warning',
    outcome: 'success',
    subjectType: 'subscription',
    subjectId: 'sub_2',
    target: 'Khan Bakery · Starter',
    ip: '10.0.4.12',
    ipCountry: 'BD',
    userAgent: 'possignin-billing/2.1',
    sessionId: 'ses_system',
    requestId: 'req_99oo00',
    metadata: { dunning_attempt: 1, next_retry_at: '2026-04-28T18:00:00Z' },
  },
  {
    id: 'al_024',
    occurredAt: '2026-04-25T15:08:11Z',
    actor: 'system',
    actorEmail: 'system@possignin',
    actorRole: 'system',
    action: 'payment.failed',
    category: 'payment',
    severity: 'warning',
    outcome: 'failure',
    subjectType: 'payment',
    subjectId: 'pay_8',
    target: 'INV-2026-0212 · Khan Bakery · ৳300',
    ip: '10.0.4.12',
    ipCountry: 'BD',
    userAgent: 'sslcommerz-webhook/3.1',
    sessionId: 'ses_system',
    requestId: 'req_aa66bb',
    metadata: { gateway_code: 'CARD_DECLINED', gateway_message: 'Insufficient funds' },
  },
  {
    id: 'al_025',
    occurredAt: '2026-04-24T13:45:08Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@rootsoftit.com',
    actorRole: 'platform_admin',
    action: 'settings.update',
    category: 'configuration',
    severity: 'info',
    outcome: 'success',
    subjectType: 'system',
    subjectId: 'platform-settings',
    target: 'Platform / Security tab',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_cc77dd',
    diff: {
      'security.password_min_length': { before: 8, after: 12 },
      'security.mfa_required_for_admin': { before: false, after: true },
    },
  },
  {
    id: 'al_026',
    occurredAt: '2026-04-23T09:22:18Z',
    actor: 'Imran Khan',
    actorEmail: 'imran@khan-bakery.com',
    actorRole: 'business_admin',
    action: 'session.revoke',
    category: 'security',
    severity: 'info',
    outcome: 'success',
    subjectType: 'session',
    subjectId: 'ses_old_5',
    target: 'Imran Khan · Windows · Edge',
    ip: '180.211.124.9',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_new_5',
    requestId: 'req_ee88ff',
    metadata: { revoked_session_id: 'ses_old_5', remaining_sessions: 2 },
  },
  {
    id: 'al_027',
    occurredAt: '2026-04-22T16:55:42Z',
    actor: 'system',
    actorEmail: 'system@possignin',
    actorRole: 'system',
    action: 'webhook.delivery_failed',
    category: 'system',
    severity: 'warning',
    outcome: 'failure',
    subjectType: 'webhook',
    subjectId: 'wh_evt_8123',
    target: 'payment.received → pos.legacy',
    ip: '10.0.4.12',
    ipCountry: 'BD',
    userAgent: 'possignin-webhooks/2.0.1',
    sessionId: 'ses_system',
    requestId: 'req_77pp88',
    metadata: { http_status: 503, attempt: 1, will_retry_at: '2026-04-22T17:55:42Z' },
  },
  {
    id: 'al_028',
    occurredAt: '2026-04-21T11:14:03Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@rootsoftit.com',
    actorRole: 'platform_admin',
    action: 'reseller.invite',
    category: 'user_management',
    severity: 'info',
    outcome: 'success',
    subjectType: 'reseller',
    subjectId: 'rsl_4',
    target: 'Beta Resellers',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_99qq00',
    metadata: { commission_pct: 20, owner_email: 'hello@beta-res.com' },
  },
  {
    id: 'al_029',
    occurredAt: '2026-04-20T23:48:51Z',
    actor: 'system',
    actorEmail: 'system@possignin',
    actorRole: 'system',
    action: 'auth.lockout',
    category: 'security',
    severity: 'critical',
    outcome: 'success',
    subjectType: 'user',
    subjectId: 'usr_10',
    target: 'Imran Khan',
    ip: '45.158.32.119',
    ipCountry: 'RU',
    userAgent: 'curl/7.88',
    sessionId: '—',
    requestId: 'req_aa88bb',
    metadata: { trigger: '10_failed_attempts', lockout_min: 15, ip_country: 'RU' },
  },
  {
    id: 'al_030',
    occurredAt: '2026-04-19T08:01:14Z',
    actor: 'Sayem Khan',
    actorEmail: 'sayem@rootsoftit.com',
    actorRole: 'platform_admin',
    action: 'coupon.create',
    category: 'configuration',
    severity: 'info',
    outcome: 'success',
    subjectType: 'coupon',
    subjectId: 'cp_1',
    target: 'EID2026 · 20% off 3mo',
    ip: '202.74.245.18',
    ipCountry: 'BD',
    userAgent: 'Mozilla/5.0 (Macintosh) Chrome/132',
    sessionId: 'ses_a1b2c3',
    requestId: 'req_cc99dd',
    metadata: { discount_pct: 20, duration_intervals: 3, max_redemptions: 200 },
  },
];

/**
 * Notifications (bell dropdown).
 */
export type Notification = {
  id: string;
  at: string;
  category: 'billing' | 'team' | 'system' | 'security';
  title: string;
  detail: string;
  unread: boolean;
  href?: string;
};

export const notifications: Notification[] = [
  { id: 'n1', at: '2 min ago', category: 'billing', title: 'Payment received', detail: 'INV-2026-0345 paid via SSLCommerz · ৳1,500', unread: true, href: '/billing' },
  { id: 'n2', at: '1 hour ago', category: 'team', title: 'New team member joined', detail: 'cashier@khan-bakery.com accepted the invitation', unread: true, href: '/team' },
  { id: 'n3', at: 'today 09:00', category: 'system', title: 'Daily summary', detail: 'Sales ৳28,500 · 87 orders · 12% above last Friday', unread: false },
  { id: 'n4', at: 'yesterday', category: 'security', title: 'New device sign-in', detail: 'iPhone · Safari · Sylhet, BD', unread: false, href: '/profile' },
];
